import React, { useEffect } from 'react'

import { Box, Divider, FormControl, InputLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material'
import { useFormik } from 'formik'

import Accordion from '../../../../shared/components/Accordion/Accordion.component'

import { GRAMS_PER_KILOGRAM, GRAMS_PER_POUND } from '../../../../shared/dictionaries/unitConversion.dictionary'
import { OverallPackageDetails } from '../../CreateFulfillmentOrder.types'
import {
  INITIAL_OVERALL_ORDER_DETAILS_VALUES,
  PACKAGE_TYPE_OPTIONS,
  USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM,
  WEIGHT_UNIT_OPTIONS,
  getOverallOrderDetailsValidationSchema
} from './OverallOrderDetails.dictionary'
import styles from './OverallOrderDetails.styles'
import { OverallOrderDetailsFormValues, OverallOrderDetailsProps } from './OverallOrderDetails.types'

const OverallOrderDetails: React.FC<OverallOrderDetailsProps> = ({ packageDetails, onPackageDetailsChange }) => {
  const resolveWeightValue = (grams: number): { value: string; unit: 'kg' | 'lbs' } => {
    if (grams <= 0) return { value: '', unit: 'lbs' }

    return { value: (grams / GRAMS_PER_POUND).toFixed(2), unit: 'lbs' }
  }

  const formik = useFormik<OverallOrderDetailsFormValues>({
    initialValues: packageDetails
      ? (() => {
          const { value, unit } = resolveWeightValue(packageDetails.totalWeightGrams)

          return {
            lengthCm: packageDetails.lengthCm.toString(),
            widthCm: packageDetails.widthCm.toString(),
            heightCm: packageDetails.heightCm.toString(),
            packageWeightValue: value,
            weightUnit: unit,
            packageType: packageDetails.packageType || 'box',
            fragileHandling: packageDetails.fragileHandling ?? false,
            repackagingAllowed: packageDetails.repackagingAllowed ?? false,
            specialHandlingNotes: packageDetails.specialHandlingNotes || ''
          }
        })()
      : INITIAL_OVERALL_ORDER_DETAILS_VALUES,
    validationSchema: getOverallOrderDetailsValidationSchema(true),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => undefined
  })

  useEffect(() => {
    const schema = getOverallOrderDetailsValidationSchema(true)
    const isValid = schema.isValidSync(formik.values)

    if (isValid) {
      const rawWeightValue = Number(formik.values.packageWeightValue)
      const totalWeightGrams =
        formik.values.weightUnit === 'lbs'
          ? Math.round(rawWeightValue * GRAMS_PER_POUND)
          : Math.round(rawWeightValue * GRAMS_PER_KILOGRAM)

      const updatedPackageDetails: OverallPackageDetails = {
        lengthCm: Number(formik.values.lengthCm),
        widthCm: Number(formik.values.widthCm),
        heightCm: Number(formik.values.heightCm),
        totalWeightGrams,
        packageType: formik.values.packageType,
        fragileHandling: formik.values.fragileHandling,
        repackagingAllowed: formik.values.repackagingAllowed,
        specialHandlingNotes: formik.values.specialHandlingNotes || null
      }

      onPackageDetailsChange(true, updatedPackageDetails)
    } else {
      onPackageDetailsChange(false, {
        lengthCm: 0,
        widthCm: 0,
        heightCm: 0,
        totalWeightGrams: 0
      })
    }
  }, [formik.values, onPackageDetailsChange])

  const getFieldError = (fieldName: keyof OverallOrderDetailsFormValues): string | undefined => {
    return formik.touched[fieldName] && formik.errors[fieldName] ? String(formik.errors[fieldName]) : undefined
  }

  const hasFieldError = (fieldName: keyof OverallOrderDetailsFormValues): boolean => {
    return Boolean(formik.touched[fieldName] && formik.errors[fieldName])
  }

  return (
    <Box sx={styles.container}>
      <Typography variant="h6" sx={styles.sectionTitle}>
        Overall Order Details
      </Typography>

      {/* Package Type - first in list */}
      <FormControl fullWidth>
        <InputLabel>Package Type *</InputLabel>
        <Select
          name="packageType"
          value={formik.values.packageType}
          label="Package Type *"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        >
          {PACKAGE_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Package Weight + Unit Row */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField
          name="packageWeightValue"
          label="Package weight *"
          value={formik.values.packageWeightValue}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={hasFieldError('packageWeightValue')}
          helperText={getFieldError('packageWeightValue')}
          fullWidth
          type="number"
          inputProps={{ min: 0.001 }}
        />

        <FormControl sx={{ minWidth: 80 }}>
          <InputLabel>Unit</InputLabel>

          <Select
            name="weightUnit"
            value={formik.values.weightUnit}
            label="Unit"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            {WEIGHT_UNIT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Dimensions */}
      <Box sx={styles.dimensionsRow}>
        <TextField
          name="lengthCm"
          label="Length (cm)"
          value={formik.values.lengthCm}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={hasFieldError('lengthCm')}
          helperText={getFieldError('lengthCm')}
          fullWidth
          type="number"
          inputProps={{ min: 1, max: Math.round(USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM) }}
        />

        <TextField
          name="widthCm"
          label="Width (cm)"
          value={formik.values.widthCm}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={hasFieldError('widthCm')}
          helperText={getFieldError('widthCm')}
          fullWidth
          type="number"
          inputProps={{ min: 1, max: Math.round(USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM) }}
        />

        <TextField
          name="heightCm"
          label="Height (cm)"
          value={formik.values.heightCm}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={hasFieldError('heightCm')}
          helperText={getFieldError('heightCm')}
          fullWidth
          type="number"
          inputProps={{ min: 1, max: Math.round(USPS_MAX_COMBINED_LENGTH_PLUS_GIRTH_CM) }}
        />
      </Box>

      <Divider sx={styles.divider} />

      {/* Delivery Preferences - collapsible, collapsed by default */}
      <Accordion
        expandIconPosition="right"
        hasPadding
        isExpanded={false}
        header={{
          mainContent: (
            <Typography variant="subtitle2" sx={styles.deliveryPreferencesTitle}>
              Delivery Preferences
            </Typography>
          )
        }}
        body={
          <>
            <Box sx={styles.toggleRow}>
              <Box sx={styles.toggleLabelContainer}>
                <Typography sx={styles.toggleLabel}>Fragile Package</Typography>

                <Typography sx={styles.toggleDescription}>
                  Package requires careful handling throughout transit
                </Typography>
              </Box>

              <Switch
                name="fragileHandling"
                checked={formik.values.fragileHandling}
                onChange={formik.handleChange}
                color="primary"
              />
            </Box>

            <Divider sx={styles.divider} />

            <Box sx={styles.toggleRow}>
              <Box sx={styles.toggleLabelContainer}>
                <Typography sx={styles.toggleLabel}>Repackaging Allowed</Typography>

                <Typography sx={styles.toggleDescription}>
                  Logistics partner may repackage for transport if needed
                </Typography>
              </Box>

              <Switch
                name="repackagingAllowed"
                checked={formik.values.repackagingAllowed}
                onChange={formik.handleChange}
                color="primary"
              />
            </Box>

            <Divider sx={styles.divider} />

            <Box sx={{ paddingTop: 1 }}>
              <Typography sx={styles.toggleLabel}>Special Handling Notes (optional)</Typography>

              <TextField
                name="specialHandlingNotes"
                placeholder="e.g. Keep upright, do not stack"
                value={formik.values.specialHandlingNotes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                multiline
                rows={2}
                sx={{ marginTop: 1 }}
              />
            </Box>
          </>
        }
      />
    </Box>
  )
}

export default OverallOrderDetails
